const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtCompact = (n) => {
  const v = Number(n ?? 0);
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(v);
};
const fmtPct = (n, digits = 1) => `${Number(n ?? 0).toFixed(digits)}%`;

const SOCIAL_COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#84cc16",
  "#f97316",
  "#6366f1",
];

const AGE_BAND_ORDER = ["<30", "30-40", "40-50", "50-55", "55-60", ">60"];

export const buildProfileRingStyle = (overview = {}) => {
  const total = overview.total_teachers ?? 0;
  const female = overview.female_count ?? 0;
  const male = overview.male_count ?? 0;
  const other = Math.max(0, total - female - male);
  const femalePct = total > 0 ? (female / total) * 100 : 0;
  const maleEnd = femalePct + (total > 0 ? (male / total) * 100 : 0);
  return {
    femalePct: Math.round(femalePct * 10) / 10,
    style: {
      background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--ctex-pink) 0 ${femalePct}%, var(--ctex-primary) ${femalePct}% ${maleEnd}%, #94a3b8 ${maleEnd}% 100%)`,
    },
  };
};

export const buildProfileHeroCopy = (overview = {}, ageData = null) => {
  const femalePct = overview.female_pct ?? 0;
  const ageing = ageData?.ageing_risk_pct ?? 0;
  return {
    headline: "Workforce Profile Snapshot",
    narrative: `The teacher workforce is strongly female-led (${fmtPct(femalePct)}), concentrated in the 40–50 age band, and academically strong. The key risk is succession planning for older cohorts and targeted support for untrained staff.`,
    chips: [
      { label: `${fmt(overview.total_teachers)} Teachers`, className: "green" },
      { label: `GPI ${overview.gender_parity_index ?? 0}`, className: "" },
      { label: `Ageing Risk ${fmtPct(ageing)}`, className: "amber" },
    ],
    primaryAction:
      "Prioritize 55+ age-band succession planning and track untrained teachers block-wise before the next staffing review.",
  };
};

export const buildProfileHeroKpis = (overview = {}, ageData = null) => {
  const rosterTotal = overview.teacher_records || overview.total_teachers || 1;
  const malePct = ((overview.male_count ?? 0) / rosterTotal) * 100;
  const femalePct = overview.female_pct ?? ((overview.female_count ?? 0) / rosterTotal) * 100;
  return [
    {
      label: "Male Teachers",
      value: fmt(overview.male_count),
      sub: `${fmtPct(malePct)} of total`,
      iconClass: "",
    },
    {
      label: "Female Teachers",
      value: fmt(overview.female_count),
      sub: `${fmtPct(femalePct)} of total`,
      iconClass: "pink",
    },
    {
      label: "Gender Parity Index",
      value: String(overview.gender_parity_index ?? 0),
      sub: "Female-to-male ratio",
      iconClass: "green",
    },
    {
      label: "Average Age",
      value: String(ageData?.avg_age ?? "—"),
      sub: "Years",
      iconClass: "purple",
    },
  ];
};

export const buildProfileGenderDonut = (overview = {}, genderData = []) => {
  const total = overview.teacher_records || overview.total_teachers || 0;
  const female = overview.female_count ?? 0;
  const male = overview.male_count ?? 0;
  const otherEntry = (genderData || []).find(
    (g) => g.gender === "Other" || g.gender === "Transgender"
  );
  const other = otherEntry?.count ?? Math.max(0, total - female - male);
  const femalePct = total > 0 ? (female / total) * 100 : 0;
  const malePct = total > 0 ? (male / total) * 100 : 0;
  const maleEnd = femalePct + malePct;
  return {
    total,
    gpi: overview.gender_parity_index ?? 0,
    style: {
      background: `conic-gradient(var(--ctex-pink) 0 ${femalePct}%, var(--ctex-primary) ${femalePct}% ${maleEnd}%, #94a3b8 ${maleEnd}% 100%)`,
    },
    legend: [
      { label: "Female Teachers", hint: "Largest workforce segment", value: female, dotClass: "pink" },
      { label: "Male Teachers", hint: "Secondary segment", value: male, dotClass: "" },
      { label: "Other", hint: "Self-reported category", value: other, dotClass: "gray" },
    ],
  };
};

export const buildProfileAgeBars = (ageData = null) => {
  const distribution = ageData?.distribution ?? [];
  const ordered = AGE_BAND_ORDER.map((band) => {
    const row = distribution.find((d) => d.age_band === band);
    return { band, count: row?.count ?? 0 };
  });
  const max = Math.max(...ordered.map((r) => r.count), 1);
  const maxHeight = 278;
  return {
    medianAge: ageData?.median_age ?? ageData?.avg_age ?? 0,
    ageingRisk: ageData?.ageing_risk_pct ?? 0,
    bars: ordered.map((row) => ({
      band: row.band,
      count: row.count,
      display: fmtCompact(row.count),
      height: Math.max(10, Math.round((row.count / max) * maxHeight)),
    })),
  };
};

export const buildProfileInsightCards = (overview = {}, qualification = null, ageData = null) => {
  const academic = qualification?.academic ?? [];
  const professional = qualification?.professional ?? [];
  const topAcademic = academic[0];
  const untrainedRow = professional.find((p) => String(p.qualification).toLowerCase().includes("none"));
  const untrainedCount = untrainedRow?.count ?? 0;
  const ageingRisk = ageData?.ageing_risk_pct ?? 0;
  const total = overview.teacher_records || overview.total_teachers || 1;

  return [
    {
      id: "academic",
      title: topAcademic ? `${topAcademic.qualification} leads the workforce` : "Academic profile available",
      body: topAcademic
        ? `${fmt(topAcademic.count)} teachers (${fmtPct((topAcademic.count / total) * 100)}) hold ${topAcademic.qualification}, shaping the academic strength profile.`
        : "Academic qualification distribution is loaded from the current teacher dataset.",
      iconClass: "green",
    },
    {
      id: "succession",
      title: ageingRisk >= 10 ? "Succession planning needed" : "Ageing risk is manageable",
      body: `District ageing risk is ${fmtPct(ageingRisk)} with average age ${ageData?.avg_age ?? "—"} years across ${fmt(overview.total_teachers)} teachers.`,
      iconClass: "purple",
    },
    {
      id: "professional",
      title: untrainedCount > 0 ? "Professional gap is actionable" : "Professional qualification base is stable",
      body:
        untrainedCount > 0
          ? `${fmt(untrainedCount)} untrained teachers (${fmtPct((untrainedCount / total) * 100)}) need certification follow-up.`
          : "No untrained professional category recorded in the current qualification distribution.",
      iconClass: "orange",
    },
  ];
};

const getBarColor = (label = "") => {
  const lower = label.toLowerCase();
  if (lower.includes("none") || lower.includes("untrained")) return "orange";
  if (lower.includes("special")) return "purple";
  return "green";
};

export const buildQualificationBars = (items = [], limit = 7) => {
  const rows = [...(items || [])].slice(0, limit);
  const max = rows[0]?.count || 1;
  return rows.map((row) => ({
    label: row.qualification,
    count: row.count,
    display: fmtCompact(row.count),
    width: Math.max(2, ((row.count ?? 0) / max) * 100),
    barClass: getBarColor(row.qualification),
  }));
};

export const buildSocialCategoryView = (socialCategory = []) => {
  const total = (socialCategory || []).reduce((s, c) => s + (c.count ?? 0), 0) || 1;
  let cursor = 0;
  const segments = (socialCategory || []).map((cat, idx) => {
    const pct = ((cat.count ?? 0) / total) * 100;
    const start = cursor;
    cursor += pct;
    return {
      category: cat.category,
      count: cat.count,
      pct: Math.round(pct * 10) / 10,
      color: cat.color || SOCIAL_COLORS[idx % SOCIAL_COLORS.length],
      start,
      end: cursor,
    };
  });
  const gradient =
    segments.length > 0
      ? `conic-gradient(${segments.map((s) => `${s.color} ${s.start}% ${s.end}%`).join(", ")})`
      : "conic-gradient(#3b82f6 0 100%)";
  return { total, segments, style: { background: gradient } };
};

export const buildProfileActions = (overview = {}, qualification = null, ageData = null, blockData = []) => {
  const ageingRisk = ageData?.ageing_risk_pct ?? 0;
  const untrainedRow = (qualification?.professional || []).find((p) =>
    String(p.qualification).toLowerCase().includes("none")
  );
  const untrainedCount = untrainedRow?.count ?? 0;
  const lowGpiBlocks = blockData.filter((b) => (b.gpi ?? 0) > 0 && (b.gpi ?? 0) < 1).length;

  return [
    {
      title: "Create 50+ Cohort Succession Map",
      body: `Track ${fmtPct(ageingRisk)} ageing risk and plan succession for blocks with older teacher cohorts.`,
      iconClass: "purple",
      priority: ageingRisk >= 10 ? "High" : "Track",
      priorityClass: ageingRisk >= 10 ? "red" : "green",
    },
    {
      title: "Resolve Untrained Teacher List",
      body:
        untrainedCount > 0
          ? `Map ${fmt(untrainedCount)} untrained teachers to certification support by block and school.`
          : "Maintain professional qualification monitoring; no untrained category currently recorded.",
      iconClass: "orange",
      priority: untrainedCount > 0 ? "Medium" : "Track",
      priorityClass: untrainedCount > 0 ? "" : "green",
    },
    {
      title: "Use GPI Lens in Deployment Review",
      body:
        lowGpiBlocks > 0
          ? `Review gender parity in ${lowGpiBlocks} block${lowGpiBlocks === 1 ? "" : "s"} with GPI below 1.0 before new postings.`
          : `District GPI is ${overview.gender_parity_index ?? 0}; continue parity checks during deployment reviews.`,
      iconClass: "green",
      priority: lowGpiBlocks > 0 ? "Medium" : "Track",
      priorityClass: lowGpiBlocks > 0 ? "" : "green",
    },
  ];
};

export const buildBlockProfileWatchlist = (
  blockData = [],
  overview = {},
  ageData = null,
  qualification = null
) => {
  const districtAgeing = ageData?.ageing_risk_pct ?? 0;
  const districtAvgAge = ageData?.avg_age ?? null;
  const total = overview.teacher_records || overview.total_teachers || 1;
  const untrainedRow = (qualification?.professional || []).find((p) =>
    String(p.qualification).toLowerCase().includes("none")
  );
  const districtUntrainedPct = ((untrainedRow?.count ?? 0) / total) * 100;

  return [...(blockData || [])]
    .filter((block) => block.block_name)
    .sort((a, b) => {
      const aScore = (a.gpi ?? 0) > 0 && (a.gpi ?? 0) < 1 ? 2 : 0;
      const bScore = (b.gpi ?? 0) > 0 && (b.gpi ?? 0) < 1 ? 2 : 0;
      return bScore - aScore || (a.gpi ?? 0) - (b.gpi ?? 0);
    })
    .slice(0, 5)
    .map((block) => {
      const gpi = block.gpi ?? 0;
      const ageingRisk = districtAgeing;
      const untrained = districtUntrainedPct;
      const blockAvgAge = districtAvgAge;

      let status = "Stable";
      let statusClass = "green";
      if (gpi > 0 && gpi < 1) {
        status = "Priority";
        statusClass = "red";
      } else if (districtAgeing >= 10 || districtUntrainedPct >= 4) {
        status = "Watch";
        statusClass = "amber";
      }

      return {
        block: block.block_name,
        teachers: block.total_teachers,
        gpi: block.gpi,
        avgAge: blockAvgAge ?? "—",
        ageingRisk: fmtPct(ageingRisk),
        untrained: fmtPct(untrained),
        status,
        statusClass,
      };
    });
};

export const buildCTTeacherProfileMarkdown = (
  overview = {},
  qualification = null,
  ageData = null,
  blockData = []
) => {
  const cards = buildProfileInsightCards(overview, qualification, ageData);
  const actions = buildProfileActions(overview, qualification, ageData, blockData);
  const hero = buildProfileHeroCopy(overview, ageData);
  return [
    "## Insights",
    `- ${hero.headline}: ${hero.narrative}`,
    ...cards.map((c) => `- ${c.title}: ${c.body}`),
    "",
    "## Priority Action Items",
    ...actions.map((a) => `- ${a.title}: ${a.body}`),
  ].join("\n");
};
